// ─── Phenotype-specific metric extraction ─────────────────────────────────────
//
// Data logic for extracting clinically relevant metrics per phenotype subtype.
// Split from PhenotypeMetricPanel.tsx to keep rendering and data logic separate.
// ──────────────────────────────────────────────────────────────────────────────

import { DailyMetrics } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MetricItem {
  label: string;
  value: string | number;
  unit?: string;
  status: 'good' | 'warning' | 'alert' | 'neutral';
  detail?: string;
  /** 7-day daily values for sparkline (omit for point-in-time labs) */
  sparkline?: number[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function getRecent(metrics: DailyMetrics[], days: number): DailyMetrics[] {
  return metrics.slice(-days);
}

// ─── Immunometabolic ─────────────────────────────────────────────────────────

export function getImmunometabolicMetrics(
  metrics: DailyMetrics[],
  labData?: Record<string, { value: number; unit: string }>,
  appleExtras?: { restingHR: number | null; weight: number | null },
): MetricItem[] {
  const items: MetricItem[] = [];
  const recent7 = getRecent(metrics, 7);

  if (labData?.['hsCRP'] || labData?.['CRP']) {
    const crp = labData['hsCRP'] || labData['CRP'];
    items.push({
      label: 'hsCRP',
      value: crp.value,
      unit: crp.unit || 'mg/L',
      status: crp.value >= 3.0 ? 'alert' : crp.value >= 1.5 ? 'warning' : 'good',
      detail: crp.value >= 3.0 ? 'High inflammation — strong immunometabolic signal'
            : crp.value >= 1.5 ? 'Elevated — monitor trend'
            : 'Within range',
    });
  }

  if (labData?.['Insulin']) {
    const ins = labData['Insulin'];
    items.push({
      label: 'Fasting Insulin',
      value: ins.value,
      unit: ins.unit || 'uIU/mL',
      status: ins.value >= 12 ? 'alert' : ins.value >= 8 ? 'warning' : 'good',
      detail: ins.value >= 12 ? 'Insulin resistance likely'
            : ins.value >= 8 ? 'Early insulin resistance'
            : 'Healthy insulin sensitivity',
    });
  }

  if (labData?.['Glucose']) {
    const glu = labData['Glucose'];
    items.push({
      label: 'Fasting Glucose',
      value: glu.value,
      unit: glu.unit || 'mg/dL',
      status: glu.value >= 126 ? 'alert' : glu.value >= 100 ? 'warning' : 'good',
      detail: glu.value >= 126 ? 'Diabetic range' : glu.value >= 100 ? 'Prediabetic' : 'Normal',
    });
  }

  if (labData?.['HbA1c']) {
    const a1c = labData['HbA1c'];
    items.push({
      label: 'HbA1c',
      value: a1c.value,
      unit: '%',
      status: a1c.value >= 6.5 ? 'alert' : a1c.value >= 5.7 ? 'warning' : 'good',
      detail: a1c.value >= 6.5 ? 'Diabetic range' : a1c.value >= 5.7 ? 'Prediabetic' : 'Normal',
    });
  }

  if (labData?.['Ferritin']) {
    const ferr = labData['Ferritin'];
    const crpVal = labData?.['hsCRP']?.value ?? labData?.['CRP']?.value ?? 0;
    const isInflammatory = ferr.value >= 200 && crpVal >= 1.5;
    const isLow = ferr.value < 30;
    items.push({
      label: 'Ferritin',
      value: ferr.value,
      unit: ferr.unit || 'ng/mL',
      status: isInflammatory ? 'alert' : isLow ? 'warning' : 'good',
      detail: isInflammatory ? 'Co-elevated with CRP — acute inflammatory pattern'
            : isLow ? 'Low iron stores — rule out deficiency as fatigue driver'
            : 'Iron stores adequate',
    });
  }

  if (labData?.['Omega-3 Index']) {
    const o3 = labData['Omega-3 Index'];
    items.push({
      label: 'Omega-3 Index',
      value: o3.value,
      unit: '%',
      status: o3.value < 4 ? 'alert' : o3.value < 8 ? 'warning' : 'good',
      detail: o3.value < 4 ? 'Low EPA+DHA — may amplify neuroinflammation'
            : o3.value < 8 ? 'Suboptimal — consider omega-3 optimization'
            : 'Optimal anti-inflammatory status',
    });
  }

  if (labData?.['Triglycerides'] && labData?.['Glucose']) {
    const tg = labData['Triglycerides'].value;
    const glu = labData['Glucose'].value;
    const tyg = Math.log((tg * glu) / 2);
    items.push({
      label: 'TyG Index',
      value: tyg.toFixed(2),
      status: tyg >= 5.0 ? 'alert' : tyg >= 4.68 ? 'warning' : 'good',
      detail: tyg >= 5.0 ? 'High insulin resistance — strong metabolic signal'
            : tyg >= 4.68 ? 'Elevated — early insulin resistance pattern'
            : 'Healthy metabolic sensitivity',
    });
  }

  if (appleExtras?.weight) {
    items.push({
      label: 'Weight',
      value: (Math.round(appleExtras.weight * 10) / 10).toFixed(1),
      unit: 'lb',
      status: 'neutral',
      detail: 'Track trends — weight gain common in immunometabolic phenotype',
    });
  }

  const sleepScores = recent7.map(m => m.sleep_score).filter((v): v is number => v != null);
  if (sleepScores.length >= 3) {
    const avg = Math.round(mean(sleepScores));
    items.push({
      label: 'Avg Sleep Score',
      value: avg,
      unit: '/100',
      status: avg < 60 ? 'alert' : avg < 74 ? 'warning' : 'good',
      detail: 'Poor sleep exacerbates insulin resistance and inflammation',
      sparkline: sleepScores,
    });
  }

  return items;
}

// ─── HPA-Dominant ────────────────────────────────────────────────────────────

export function getHPAMetrics(
  metrics: DailyMetrics[],
  labData?: Record<string, { value: number; unit: string }>,
): MetricItem[] {
  const items: MetricItem[] = [];
  const recent7 = getRecent(metrics, 7);

  if (labData?.['Cortisol']) {
    const cort = labData['Cortisol'];
    items.push({
      label: 'AM Cortisol',
      value: cort.value,
      unit: cort.unit || 'ug/dL',
      status: cort.value >= 20 ? 'alert' : cort.value >= 16 ? 'warning' : cort.value < 6 ? 'warning' : 'good',
      detail: cort.value >= 20 ? 'Elevated — HPA overactivation signal'
            : cort.value < 6 ? 'Low — possible HPA fatigue'
            : 'Within range',
    });
  }

  const hrvValues = recent7.map(m => m.hrv).filter((v): v is number => v != null && v > 0);
  if (hrvValues.length >= 3) {
    const avg = Math.round(mean(hrvValues));
    items.push({
      label: 'Avg HRV',
      value: avg,
      unit: 'ms',
      status: avg < 30 ? 'alert' : avg < 50 ? 'warning' : 'good',
      detail: 'Low HRV reflects chronic sympathetic activation from HPA overload',
      sparkline: hrvValues,
    });
  }

  const stressValues = recent7.map(m => m.stress_score).filter((v): v is number => v != null);
  if (stressValues.length >= 3) {
    const avg = Math.round(mean(stressValues));
    items.push({
      label: 'Stress Resilience',
      value: avg,
      unit: '/100',
      status: avg < 40 ? 'alert' : avg < 60 ? 'warning' : 'good',
      detail: avg < 40 ? 'High physiological stress — prioritize recovery' : 'Monitor stress trends',
      sparkline: stressValues,
    });
  }

  const sleepEff = recent7.map(m => m.sleep_efficiency).filter((v): v is number => v != null);
  if (sleepEff.length >= 3) {
    const avg = Math.round(mean(sleepEff));
    items.push({
      label: 'Sleep Efficiency',
      value: avg,
      unit: '%',
      status: avg < 70 ? 'alert' : avg < 80 ? 'warning' : 'good',
      detail: 'Fragmented sleep is a hallmark of HPA hyperactivation',
      sparkline: sleepEff,
    });
  }

  const sleepScores = recent7.map(m => m.sleep_score).filter((v): v is number => v != null);
  if (sleepScores.length >= 3) {
    const avg = Math.round(mean(sleepScores));
    items.push({
      label: 'Avg Sleep Score',
      value: avg,
      unit: '/100',
      status: avg < 60 ? 'alert' : avg < 74 ? 'warning' : 'good',
      detail: 'Stress-driven sleep disruption is a treatment priority',
    });
  }

  const lowestHR = recent7.map(m => m.lowest_hr).filter((v): v is number => v != null);
  if (lowestHR.length >= 3) {
    const avg = Math.round(mean(lowestHR));
    items.push({
      label: 'Lowest HR (Sleep)',
      value: avg,
      unit: 'bpm',
      status: avg > 60 ? 'warning' : avg > 55 ? 'neutral' : 'good',
      detail: 'Elevated nocturnal HR suggests sympathetic overdrive',
    });
  }

  if (labData?.['TSH']) {
    const tsh = labData['TSH'];
    items.push({
      label: 'TSH',
      value: tsh.value,
      unit: tsh.unit || 'mIU/L',
      status: tsh.value >= 4.0 ? 'alert' : tsh.value >= 2.5 ? 'warning' : 'good',
      detail: tsh.value >= 4.0 ? 'Hypothyroid range — rule out before HPA attribution'
            : tsh.value >= 2.5 ? 'Subclinical range — may contribute to stress symptoms'
            : 'Thyroid function normal',
    });
  }

  return items;
}

// ─── Anhedonic ───────────────────────────────────────────────────────────────

export function getAnhedonicMetrics(
  metrics: DailyMetrics[],
  labData?: Record<string, { value: number; unit: string }>,
): MetricItem[] {
  const items: MetricItem[] = [];
  const recent7 = getRecent(metrics, 7);
  const recent14 = getRecent(metrics, 14);

  const steps7 = recent7.map(m => m.steps).filter((v): v is number => v != null);
  const steps14 = recent14.map(m => m.steps).filter((v): v is number => v != null);
  if (steps7.length >= 3) {
    const avg = Math.round(mean(steps7));
    const baseline = steps14.length >= 7 ? Math.round(mean(steps14)) : null;
    const dropPct = baseline ? Math.round(((baseline - avg) / baseline) * 100) : null;
    items.push({
      label: 'Avg Daily Steps',
      value: avg.toLocaleString(),
      status: avg < 3000 ? 'alert' : avg < 5000 ? 'warning' : 'good',
      detail: dropPct && dropPct > 20
        ? `${dropPct}% below 14-day baseline — declining engagement`
        : avg < 3000
        ? 'Very low activity — behavioral activation may help'
        : 'Maintaining activity levels',
    });
  }

  const exercise7 = recent7.map(m => m.exercise_mins).filter((v): v is number => v != null);
  if (exercise7.length >= 3) {
    const avg = Math.round(mean(exercise7));
    items.push({
      label: 'Avg Exercise',
      value: avg,
      unit: 'min/day',
      status: avg < 10 ? 'alert' : avg < 20 ? 'warning' : 'good',
      detail: 'Exercise is a first-line intervention for anhedonia via dopamine pathways',
    });
  }

  const readiness7 = recent7.map(m => m.readiness).filter((v): v is number => v != null);
  if (readiness7.length >= 3) {
    const avg = Math.round(mean(readiness7));
    items.push({
      label: 'Avg Readiness',
      value: avg,
      unit: '/100',
      status: avg < 60 ? 'alert' : avg < 72 ? 'warning' : 'good',
      detail: 'Low readiness may reflect reward-motivation deficit',
    });
  }

  const sleepDur7 = recent7.map(m => m.total_sleep_mins).filter((v): v is number => v != null);
  if (sleepDur7.length >= 3) {
    const avg = Math.round(mean(sleepDur7));
    const hours = Math.floor(avg / 60);
    const mins = avg % 60;
    items.push({
      label: 'Avg Sleep Duration',
      value: `${hours}h ${mins}m`,
      status: avg > 600 ? 'warning' : avg < 300 ? 'alert' : 'good',
      detail: avg > 600 ? 'Hypersomnia — common in anhedonic phenotype' : avg < 300 ? 'Very low sleep' : 'Healthy range',
    });
  }

  const deep7 = recent7.map(m => m.deep_sleep_mins).filter((v): v is number => v != null);
  if (deep7.length >= 3) {
    const avg = Math.round(mean(deep7));
    items.push({
      label: 'Avg Deep Sleep',
      value: avg,
      unit: 'min',
      status: avg < 45 ? 'alert' : avg < 60 ? 'warning' : 'good',
      detail: 'Deep sleep is critical for dopamine receptor restoration',
    });
  }

  if (labData?.['Vitamin D']) {
    const vd = labData['Vitamin D'];
    items.push({
      label: 'Vitamin D',
      value: vd.value,
      unit: vd.unit || 'ng/mL',
      status: vd.value < 20 ? 'alert' : vd.value < 30 ? 'warning' : 'good',
      detail: vd.value < 20 ? 'Deficient — correct before interpreting anhedonia'
            : vd.value < 30 ? 'Insufficient — optimize for mood support'
            : 'Adequate',
    });
  }

  if (labData?.['Vitamin B12']) {
    const b12 = labData['Vitamin B12'];
    items.push({
      label: 'Vitamin B12',
      value: b12.value,
      unit: b12.unit || 'pg/mL',
      status: b12.value < 300 ? 'alert' : b12.value < 500 ? 'warning' : 'good',
      detail: b12.value < 300 ? 'Deficient — address as Tier 1 priority'
            : b12.value < 500 ? 'Suboptimal for cognitive function'
            : 'Adequate',
    });
  }

  if (labData?.['Folate']) {
    const fol = labData['Folate'];
    items.push({
      label: 'Folate',
      value: fol.value,
      unit: fol.unit || 'ng/mL',
      status: fol.value < 2 ? 'alert' : fol.value < 4 ? 'warning' : 'good',
      detail: fol.value < 2 ? 'Frank deficiency — evaluate alongside B12'
            : fol.value < 4 ? 'Low — relevant to methylation and mood'
            : 'Adequate',
    });
  }

  return items;
}

// ─── Anxious ─────────────────────────────────────────────────────────────────

export function getAnxiousMetrics(
  metrics: DailyMetrics[],
  appleExtras?: { restingHR: number | null; weight: number | null },
): MetricItem[] {
  const items: MetricItem[] = [];
  const recent7 = getRecent(metrics, 7);

  const hrvValues = recent7.map(m => m.hrv).filter((v): v is number => v != null && v > 0);
  if (hrvValues.length >= 3) {
    const avg = Math.round(mean(hrvValues));
    items.push({
      label: 'Avg HRV',
      value: avg,
      unit: 'ms',
      status: avg < 30 ? 'alert' : avg < 50 ? 'warning' : 'good',
      detail: 'Low HRV is associated with reduced autonomic recovery and elevated physiological arousal',
    });
  }

  if (appleExtras?.restingHR) {
    const rhr = Math.round(appleExtras.restingHR);
    items.push({
      label: 'Resting HR',
      value: rhr,
      unit: 'bpm',
      status: rhr > 80 ? 'alert' : rhr > 70 ? 'warning' : 'good',
      detail: 'Elevated resting HR may reflect increased sympathetic nervous system activation',
    });
  }

  const lowestHR = recent7.map(m => m.lowest_hr).filter((v): v is number => v != null);
  if (lowestHR.length >= 3) {
    const avg = Math.round(mean(lowestHR));
    items.push({
      label: 'Lowest HR (Sleep)',
      value: avg,
      unit: 'bpm',
      status: avg > 60 ? 'warning' : avg > 55 ? 'neutral' : 'good',
      detail: 'Elevated nocturnal HR may reflect heightened physiological arousal during sleep',
    });
  }

  const stressValues = recent7.map(m => m.stress_score).filter((v): v is number => v != null);
  if (stressValues.length >= 3) {
    const avg = Math.round(mean(stressValues));
    items.push({
      label: 'Stress Resilience',
      value: avg,
      unit: '/100',
      status: avg < 40 ? 'alert' : avg < 60 ? 'warning' : 'good',
      detail: 'Sustained physiological stress can reduce resilience over time',
    });
  }

  const sleepScores = recent7.map(m => m.sleep_score).filter((v): v is number => v != null);
  if (sleepScores.length >= 3) {
    const avg = Math.round(mean(sleepScores));
    items.push({
      label: 'Avg Sleep Score',
      value: avg,
      unit: '/100',
      status: avg < 60 ? 'alert' : avg < 74 ? 'warning' : 'good',
      detail: 'Sleep disruption associated with elevated arousal is a common focus area',
    });
  }

  const sleepEff = recent7.map(m => m.sleep_efficiency).filter((v): v is number => v != null);
  if (sleepEff.length >= 3) {
    const avg = Math.round(mean(sleepEff));
    items.push({
      label: 'Sleep Efficiency',
      value: avg,
      unit: '%',
      status: avg < 70 ? 'alert' : avg < 80 ? 'warning' : 'good',
      detail: 'Low efficiency suggests difficulty falling or staying asleep',
    });
  }

  return items;
}

// ─── Precision Mechanism Targets ─────────────────────────────────────────────
// Biological pathways implicated in each phenotype — NOT treatment advice.

export const MECHANISM_TARGETS: Record<string, { areas: string[]; color: string }> = {
  immunometabolic: {
    areas: [
      'Pro-inflammatory cytokine pathway (IL-6, TNF-α, CRP)',
      'Insulin resistance & metabolic signaling',
      'Kynurenine pathway (tryptophan metabolism)',
      'Gut–brain axis & microbiome composition',
      'Oxidative stress & mitochondrial function',
    ],
    color: '#EF4444',
  },
  hpa_dominant: {
    areas: [
      'HPA axis dysregulation (cortisol rhythm)',
      'Autonomic imbalance (low vagal tone)',
      'Glucocorticoid receptor sensitivity',
      'Sleep architecture disruption (slow-wave deficit)',
      'Prefrontal–amygdala connectivity',
    ],
    color: '#F59E0B',
  },
  anhedonic: {
    areas: [
      'Mesolimbic dopamine pathway (reward circuitry)',
      'Ventral striatum hypoactivation',
      'Noradrenergic signaling (locus coeruleus)',
      'Circadian rhythm misalignment',
      'BDNF & neuroplasticity markers',
    ],
    color: '#8B5CF6',
  },
  anxious: {
    areas: [
      'Amygdala hyperreactivity & threat processing',
      'GABAergic tone & serotonin transporter function',
      'Autonomic nervous system (sympathetic overdrive)',
      'Default mode network rumination patterns',
      'HRV as vagal brake index',
    ],
    color: '#1E6FBF',
  },
};
